import React, { useMemo, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { NotificationsActiveOutlined } from '@mui/icons-material';
import { sendBillingPaymentReminder } from '../services/billing';

const money = (value, currency) => new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'en-ZA', {
  style: 'currency', currency: currency || 'ZAR',
}).format(Number(value || 0));

export const resolveOutstandingBalance = (invoice = {}) => {
  const recorded = invoice.balanceDue;
  if (recorded !== null && recorded !== undefined && recorded !== '' && Number.isFinite(Number(recorded))) {
    return Math.max(0, Number(recorded));
  }
  return Math.max(0, Number(invoice.total || 0) - Number(invoice.amountPaid || 0));
};

export default function BillingPaymentReminderButton({ invoice }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const balance = resolveOutstandingBalance(invoice);
  const eligible = useMemo(() => {
    if (!invoice || !['issued', 'sent', 'viewed', 'overdue'].includes(invoice.status)) return false;
    const due = invoice.dueAt ? new Date(invoice.dueAt) : null;
    return Boolean(due && !Number.isNaN(due.getTime()) && due < new Date() && balance > 0);
  }, [invoice, balance]);
  if (!eligible) return null;
  const send = async () => {
    setBusy(true); setFeedback(null);
    try {
      const result = await sendBillingPaymentReminder(invoice._id);
      setOpen(false); setFeedback({ severity: 'success', message: result.message || 'Payment reminder queued.' });
    } catch (error) {
      setFeedback({ severity: 'error', message: error.response?.data?.message || 'Unable to send the payment reminder.' });
    } finally { setBusy(false); }
  };
  return <Stack spacing={1} alignItems="flex-end">
    {feedback && <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>{feedback.message}</Alert>}
    <Button variant="outlined" color="warning" startIcon={<NotificationsActiveOutlined />} onClick={() => setOpen(true)}>Send payment reminder</Button>
    <Dialog open={open} onClose={() => !busy && setOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>Send overdue payment reminder?</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
        <Alert severity="warning">This sends an email to the billing client and records the reminder in the invoice audit history.</Alert>
        <Typography><strong>Invoice:</strong> {invoice.invoiceNumber}</Typography>
        <Typography><strong>Outstanding:</strong> {money(balance, invoice.currency)}</Typography>
        <Typography><strong>Due:</strong> {new Date(invoice.dueAt).toLocaleDateString('en-ZA')}</Typography>
      </Stack></DialogContent>
      <DialogActions><Button onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
        <Button variant="contained" color="warning" onClick={send} disabled={busy}>{busy ? 'Queueing...' : 'Send reminder'}</Button>
      </DialogActions>
    </Dialog>
  </Stack>;
}
